<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { vOnClickOutside } from '@vueuse/components';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import { useDealStagesStore } from 'dashboard/stores/dealStages';
import { useDealsStore } from 'dashboard/stores/deals';
import ContactAPI from 'dashboard/api/contacts';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  deal: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['edit', 'delete', 'schedule', 'tasks']);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const dealStagesStore = useDealStagesStore();
const dealsStore = useDealsStore();

const menuOpen = ref(false);

const toggleMenu = () => {
  menuOpen.value = !menuOpen.value;
};

const closeMenu = () => {
  menuOpen.value = false;
};

const handleEdit = () => {
  closeMenu();
  emit('edit', props.deal);
};

const handleDelete = () => {
  closeMenu();
  emit('delete', props.deal);
};

const handleSchedule = () => {
  closeMenu();
  emit('schedule', props.deal);
};

const handleTasks = () => {
  closeMenu();
  emit('tasks', props.deal);
};

const contactName = computed(() => props.deal.contact?.name || '');

// O título do card, por padrão, é o próprio nome do contato — só mostramos
// como linha extra quando for um título customizado (evita nome duplicado).
const showTitle = computed(
  () => props.deal.title && props.deal.title !== contactName.value
);

const formattedValue = computed(() => {
  if (!props.deal.valueCents && props.deal.valueCents !== 0) return null;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: props.deal.currency || 'BRL',
  }).format(props.deal.valueCents / 100);
});

const assigneeName = computed(() => props.deal.assignee?.name || '');

// TZ-safe: compare ISO date strings ('YYYY-MM-DD') directly instead of
// Date objects, so the browser's local timezone (Brazil) never shifts
// "today" by a day around midnight.
const followUpStatus = computed(() => {
  if (!props.deal.nextFollowUpOn) return null;
  const today = new Date().toLocaleDateString('en-CA');
  if (props.deal.nextFollowUpOn < today) return 'overdue';
  if (props.deal.nextFollowUpOn === today) return 'today';
  return 'future';
});

const formattedFollowUpDate = computed(() => {
  if (!props.deal.nextFollowUpOn) return '';
  return new Date(`${props.deal.nextFollowUpOn}T00:00:00`).toLocaleDateString();
});

const followUpClass = computed(() => {
  switch (followUpStatus.value) {
    case 'overdue':
      return 'text-n-ruby-9';
    case 'today':
      return 'text-n-amber-11';
    default:
      return 'text-n-slate-11';
  }
});

const stageOptions = computed(() =>
  dealStagesStore.orderedStages.map(stage => ({
    value: stage.id,
    label: stage.name,
  }))
);

const handleStageChange = newStageId => {
  if (!newStageId || newStageId === props.deal.dealStageId) return;
  dealsStore.moveToStage(props.deal.id, newStageId);
};

const contactId = computed(
  () => props.deal.contactId || props.deal.contact?.id
);

// Cards criados manualmente não têm conversa vinculada, e o card não sabe de
// antemão se o contato já tem alguma conversa — em ambos os casos, ao clicar
// buscamos as conversas do contato (canal-agnóstico) e abrimos a mais
// recente. A conta sempre usa o display_id (número sequencial por conta) na
// URL da conversa, nunca o id do banco.
const openConversation = async () => {
  closeMenu();
  const accountId = route.params.accountId;

  if (props.deal.conversationDisplayId) {
    router.push({
      path: frontendURL(
        conversationUrl({ accountId, id: props.deal.conversationDisplayId })
      ),
    });
    return;
  }

  try {
    const { data } = await ContactAPI.getConversations(contactId.value);
    const conversations = data?.payload || [];
    if (conversations.length) {
      // O jbuilder de conversas (`_conversation.json.jbuilder`) grava
      // `json.id conversation.display_id` — ou seja, o `id` deste payload
      // JÁ É o display_id. Não existe campo `display_id` aqui (diferente do
      // deal, que expõe `conversation_display_id` à parte).
      const latest = [...conversations].sort(
        (a, b) =>
          (b.last_activity_at || 0) - (a.last_activity_at || 0) || b.id - a.id
      )[0];
      router.push({
        path: frontendURL(conversationUrl({ accountId, id: latest.id })),
      });
    } else {
      useAlert(t('CRM_FUNIL.CARD.NO_CONVERSATION'));
    }
  } catch (error) {
    useAlert(t('CRM_FUNIL.CARD.NO_CONVERSATION'));
  }
};

const openContact = () => {
  closeMenu();
  const accountId = route.params.accountId;
  if (!contactId.value || !accountId) return;
  router.push({
    name: 'contacts_edit',
    params: { accountId, contactId: contactId.value },
  });
};

defineExpose({
  menuOpen,
  toggleMenu,
  handleEdit,
  handleDelete,
  handleSchedule,
  handleTasks,
  openConversation,
  openContact,
  followUpStatus,
});
</script>

<template>
  <div
    class="relative flex flex-col gap-1.5 p-2.5 bg-n-solid-1 border rounded-lg shadow-sm"
    :class="followUpStatus === 'overdue' ? 'border-n-ruby-6' : 'border-n-weak'"
  >
    <div v-on-click-outside="closeMenu">
      <button
        type="button"
        class="absolute flex items-center justify-center border rounded text-n-slate-12 bg-n-solid-1 border-n-weak hover:bg-n-alpha-2 top-1.5 right-1.5 size-5"
        :aria-label="t('CRM_FUNIL.CARD.ACTIONS')"
        @click="toggleMenu"
      >
        <i class="i-lucide-ellipsis size-4" />
      </button>
      <div
        v-if="menuOpen"
        class="absolute right-0 z-20 flex flex-col p-1 border rounded-lg shadow-lg top-6 min-w-28 bg-n-solid-1 border-n-weak"
      >
        <button
          type="button"
          class="px-2 py-1 text-xs text-left rounded text-n-slate-12 hover:bg-n-alpha-2"
          @click="handleEdit"
        >
          {{ t('CRM_FUNIL.CARD.EDIT') }}
        </button>
        <button
          type="button"
          class="px-2 py-1 text-xs text-left rounded text-n-slate-12 hover:bg-n-alpha-2"
          @click="openContact"
        >
          {{ t('CRM_FUNIL.CARD.OPEN_CONTACT') }}
        </button>
        <button
          type="button"
          class="px-2 py-1 text-xs text-left rounded text-n-slate-12 hover:bg-n-alpha-2"
          @click="openConversation"
        >
          {{ t('CRM_FUNIL.CARD.OPEN_CONVERSATION') }}
        </button>
        <button
          type="button"
          class="px-2 py-1 text-xs text-left rounded text-n-slate-12 hover:bg-n-alpha-2"
          @click="handleSchedule"
        >
          {{ t('CRM_FUNIL.CARD.SCHEDULE') }}
        </button>
        <button
          type="button"
          class="px-2 py-1 text-xs text-left rounded text-n-slate-12 hover:bg-n-alpha-2"
          @click="handleTasks"
        >
          {{ t('CRM_FUNIL.CARD.TASKS') }}
        </button>
        <button
          type="button"
          class="px-2 py-1 text-xs text-left rounded text-n-ruby-9 hover:bg-n-alpha-2"
          @click="handleDelete"
        >
          {{ t('CRM_FUNIL.CARD.DELETE') }}
        </button>
      </div>
    </div>

    <div class="flex items-center gap-2 pr-5 min-w-0">
      <Avatar :src="deal.contact?.thumbnail" :name="contactName" :size="20" />
      <span class="text-sm font-medium text-n-slate-12 truncate">
        {{ contactName }}
      </span>
    </div>
    <p v-if="showTitle" class="text-xs text-n-slate-11 truncate">
      {{ deal.title }}
    </p>
    <p v-if="formattedValue" class="text-xs font-medium text-n-slate-12">
      {{ formattedValue }}
    </p>
    <p v-if="deal.notes" class="text-xs text-n-slate-11 line-clamp-2">
      {{ deal.notes }}
    </p>
    <span v-if="assigneeName" class="text-xs text-n-slate-10 truncate">
      {{ assigneeName }}
    </span>
    <div
      v-if="deal.nextFollowUpOn"
      class="flex items-center gap-1 text-xs"
      :class="followUpClass"
    >
      <i class="i-lucide-calendar-clock size-3.5 shrink-0" />
      <span>{{ formattedFollowUpDate }}</span>
      <span v-if="followUpStatus === 'overdue'">
        · {{ t('CRM_FUNIL.FOLLOW_UP.OVERDUE') }}
      </span>
      <span v-else-if="followUpStatus === 'today'">
        · {{ t('CRM_FUNIL.FOLLOW_UP.TODAY') }}
      </span>
    </div>
    <Select
      :model-value="deal.dealStageId"
      :options="stageOptions"
      :aria-label="t('CRM_FUNIL.CARD.MOVE_TO_STAGE')"
      class="w-full"
      @update:model-value="handleStageChange"
    />
  </div>
</template>
